import React from "react";
import { PRESS, REPRESENTATION } from "../data/site";
import { ExternalLink, Quote, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Press() {
  const featured = PRESS.filter((p) => p.featured);
  const rest = PRESS.filter((p) => !p.featured);

  return (
    <div data-testid="press-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">Media & Press</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Press & PR.
        </h1>
        <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))] text-lg leading-relaxed">
          Selected features and interviews. For image requests, exhibition
          history, or interview inquiries, please contact the studio.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href={`${API}/press-kit`}
            className="inline-flex items-center gap-3 bg-foreground text-background px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90 transition-opacity"
            data-testid="press-kit-download"
          >
            <Download className="h-4 w-4" />
            Download Press Kit (PDF)
          </a>
          <span className="self-center label text-[hsl(var(--ink-soft))]">
            Bio · CV · Selected works · Coverage
          </span>
        </div>
      </header>

      {featured.length > 0 && (
        <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="press-featured">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
            {featured.map((p, i) => (
              <article
                key={i}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center"
                data-testid={`press-feature-${i}`}
              >
                <figure className="md:col-span-6">
                  <div className="aspect-[4/5] md:aspect-square overflow-hidden bg-background">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <figcaption className="label mt-3">
                    Featured in {p.outlet} · {p.author}
                  </figcaption>
                </figure>

                <div className="md:col-span-6">
                  <p className="label mb-4">
                    {p.outlet} · {p.date}
                  </p>
                  <h2 className="serif text-4xl md:text-5xl tracking-tight font-light leading-[1.05]">
                    {p.title}
                  </h2>
                  <Quote className="h-6 w-6 mt-8 text-[hsl(var(--accent))]" />
                  <blockquote className="serif italic text-xl md:text-2xl mt-3 leading-snug text-foreground">
                    {p.pull}
                  </blockquote>
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-8 inline-flex items-center gap-2 label link-underline"
                    data-testid={`press-feature-link-${i}`}
                  >
                    Read the full feature <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-24">
          <p className="label mb-6">Additional Coverage</p>
          <ul className="divide-y divide-border border-y border-border">
            {rest.map((p, i) => (
              <li
                key={i}
                className="py-6 md:py-8 grid grid-cols-12 gap-4 items-baseline"
                data-testid={`press-item-${i}`}
              >
                <span className="col-span-2 md:col-span-1 label">{p.date}</span>
                <span className="col-span-10 md:col-span-3 serif text-xl">
                  {p.outlet}
                </span>
                <div className="col-span-12 md:col-span-7">
                  <p className="serif italic text-xl md:text-2xl text-foreground leading-tight">
                    {p.title}
                  </p>
                  {p.pull && (
                    <p className="text-sm text-[hsl(var(--ink-soft))] mt-2 max-w-2xl">
                      "{p.pull}"
                    </p>
                  )}
                </div>
                <a
                  href={p.link}
                  target="_blank"
                  rel="noreferrer"
                  className="col-span-12 md:col-span-1 md:text-right text-foreground link-underline label inline-flex items-center gap-1 justify-end"
                >
                  Read <ExternalLink className="h-3 w-3" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {REPRESENTATION && REPRESENTATION.length > 0 && (
        <section className="border-t border-border bg-[hsl(var(--surface))]" data-testid="representation">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <p className="label">Representation & Profiles</p>
              <h2 className="serif text-3xl md:text-4xl mt-3 tracking-tight font-light">
                Where to find the work.
              </h2>
            </div>
            <ul className="md:col-span-8 divide-y divide-border border-y border-border">
              {REPRESENTATION.map((r, i) => (
                <li
                  key={i}
                  className="py-5 flex items-baseline justify-between gap-6"
                  data-testid={`representation-${i}`}
                >
                  <div>
                    <p className="serif text-2xl">{r.name}</p>
                    <p className="label mt-1">{r.role}</p>
                  </div>
                  <a
                    href={r.link}
                    target="_blank"
                    rel="noreferrer"
                    className="label link-underline inline-flex items-center gap-1 shrink-0"
                  >
                    Visit <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="border-t border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <p className="label">Press Contact</p>
          </div>
          <div className="md:col-span-8">
            <h3 className="serif text-3xl md:text-4xl tracking-tight font-light">
              For interviews, image requests, and features.
            </h3>
            <p className="mt-4 text-[hsl(var(--ink-soft))] leading-relaxed">
              Please mention "Press" in the subject line. High-resolution
              images, biography, and CV available upon request.
            </p>
            <a
              href="mailto:hello@josephgravesjr.com?subject=Press%20Inquiry"
              className="mt-6 inline-flex items-center gap-3 bg-foreground text-background px-7 py-3.5 text-xs uppercase tracking-[0.18em] hover:opacity-90"
              data-testid="press-email-cta"
            >
              hello@josephgravesjr.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
